import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout';
import {
  Users, Video, UserCheck, Clock, Eye, Heart, TrendingUp,
  CreditCard, Tag, Bell, RefreshCw, ChevronRight, Star, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://yetutv-backend.onrender.com';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const StatCard = ({ icon: Icon, label, value, sub, color = 'text-primary', to }) => (
  <div className="bg-card border border-border rounded-xl p-5 flex flex-col gap-2">
    <div className={`flex items-center gap-2 ${color}`}>
      <Icon className="w-5 h-5" />
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
    </div>
    <p className="text-3xl font-bold">{value ?? '—'}</p>
    {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    {to && (
      <Link to={to} className="text-xs text-primary flex items-center gap-1 mt-1 hover:underline">
        Voir tout <ChevronRight className="w-3 h-3" />
      </Link>
    )}
  </div>
);

export const AdminDashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/stats/full`, { headers: getAuthHeaders() });
      setStats(res.data);
    } catch (err) {
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStats(); }, []);

  return (
    <AdminLayout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Tableau de bord</h1>
            <p className="text-muted-foreground text-sm">Vue d'ensemble de la plateforme YetuTv</p>
          </div>
          <button
            onClick={fetchStats}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-muted rounded-lg text-sm hover:bg-muted/80 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
        </div>

        {loading && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-card border border-border rounded-xl p-5 animate-pulse h-28" />
            ))}
          </div>
        )}

        {stats && (
          <>
            {/* Alertes */}
            {(stats.videos.pending > 0 || stats.payouts.pending > 0 || stats.creator_requests_pending > 0) && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 flex flex-wrap gap-4">
                <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5" />
                <div className="flex flex-wrap gap-4 text-sm">
                  {stats.videos.pending > 0 && (
                    <Link to="/admin/videos?approved=false" className="text-yellow-600 hover:underline font-medium">
                      {stats.videos.pending} vidéo(s) en attente d'approbation
                    </Link>
                  )}
                  {stats.payouts.pending > 0 && (
                    <Link to="/admin/payouts" className="text-yellow-600 hover:underline font-medium">
                      {stats.payouts.pending} demande(s) de paiement en attente
                    </Link>
                  )}
                  {stats.creator_requests_pending > 0 && (
                    <Link to="/admin/creators" className="text-yellow-600 hover:underline font-medium">
                      {stats.creator_requests_pending} demande(s) créateur en attente
                    </Link>
                  )}
                </div>
              </div>
            )}

            {/* Stats utilisateurs */}
            <section>
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">Utilisateurs</h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={Users} label="Total" value={stats.users.total} to="/admin/users" />
                <StatCard icon={UserCheck} label="Créateurs" value={stats.users.creators} color="text-green-500" to="/admin/users?role=creator" />
                <StatCard icon={Users} label="Spectateurs" value={stats.users.viewers} color="text-blue-500" />
                <StatCard icon={Clock} label="Demandes créateurs" value={stats.creator_requests_pending} color="text-orange-500" to="/admin/creators" />
              </div>
            </section>

            {/* Stats vidéos */}
            <section>
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">Contenus</h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={Video} label="Total vidéos" value={stats.videos.total} to="/admin/videos" />
                <StatCard icon={Eye} label="Vues totales" value={stats.videos.total_views.toLocaleString()} color="text-purple-500" />
                <StatCard icon={Heart} label="Likes totaux" value={stats.videos.total_likes.toLocaleString()} color="text-red-500" />
                <StatCard icon={TrendingUp} label="Heures regardées" value={`${stats.videos.total_watch_time_hours}h`} color="text-teal-500" />
              </div>
            </section>

            {/* Stats paiements & promo */}
            <section>
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">Finance & Promotions</h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={CreditCard} label="Paiements en attente" value={stats.payouts.pending} color="text-orange-500" to="/admin/payouts" />
                <StatCard icon={CreditCard} label="Total versé (CDF)" value={stats.payouts.completed_amount.toLocaleString()} color="text-green-500" />
                <StatCard icon={Tag} label="Codes promo actifs" value={stats.promo.active} color="text-indigo-500" to="/admin/promo" />
                <StatCard icon={Bell} label="Parrainages" value={stats.engagement.total_referrals} color="text-pink-500" />
              </div>
            </section>

            {/* Tables côte à côte */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Derniers utilisateurs */}
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                <div className="flex items-center justify-between p-4 border-b border-border">
                  <h3 className="font-semibold text-sm">Derniers inscrits</h3>
                  <Link to="/admin/users" className="text-xs text-primary hover:underline">Voir tout</Link>
                </div>
                <div className="divide-y divide-border">
                  {stats.recent_users.map(u => (
                    <div key={u.id} className="flex items-center justify-between px-4 py-3">
                      <div>
                        <p className="text-sm font-medium">{u.username}</p>
                        <p className="text-xs text-muted-foreground">{u.email}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                        u.role === 'superadmin' ? 'bg-red-500/10 text-red-500' :
                        u.role === 'creator' ? 'bg-green-500/10 text-green-500' :
                        'bg-muted text-muted-foreground'
                      }`}>{u.role}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top vidéos */}
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                <div className="flex items-center justify-between p-4 border-b border-border">
                  <h3 className="font-semibold text-sm">Top 5 vidéos</h3>
                  <Link to="/admin/videos" className="text-xs text-primary hover:underline">Voir tout</Link>
                </div>
                <div className="divide-y divide-border">
                  {stats.top_videos.map((v, i) => (
                    <div key={v.id} className="flex items-center gap-3 px-4 py-3">
                      <span className="text-lg font-bold text-muted-foreground w-6">#{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{v.title}</p>
                        <p className="text-xs text-muted-foreground">par {v.creator_username} · {v.category}</p>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Eye className="w-3 h-3" />
                        {v.views_count.toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Journal récent */}
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-border">
                <h3 className="font-semibold text-sm">Activité récente des admins</h3>
                <Link to="/admin/logs" className="text-xs text-primary hover:underline">Voir tout</Link>
              </div>
              <div className="divide-y divide-border">
                {stats.recent_logs.length === 0 && (
                  <p className="text-sm text-muted-foreground p-4 text-center">Aucune activité enregistrée</p>
                )}
                {stats.recent_logs.map(log => (
                  <div key={log.id} className="flex items-start gap-3 px-4 py-3">
                    <div className="w-2 h-2 rounded-full bg-primary mt-1.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm">
                        <span className="font-medium">{log.admin_username}</span>
                        <span className="text-muted-foreground"> — {log.action.replace(/_/g, ' ')}</span>
                      </p>
                      {log.details && <p className="text-xs text-muted-foreground truncate">{log.details}</p>}
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {log.created_at ? new Date(log.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};
